import type { JSX } from 'react';
import * as React from 'react';

import {
  FloatingArrow,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import classNames from 'classnames';

import styles from './FloatingPopover.module.css';

// FloatingPopover component using Floating UI for better positioning
interface FloatingPopoverProps {
  children?: React.ReactNode;
  content: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  offset?: number;
  className?: string;
  contentClassName?: string;
  /** Accessible name for a trigger that is not itself a button. */
  triggerLabel?: string;
}

export function FloatingPopover({
  children,
  content,
  placement = 'bottom',
  offset: offsetValue = 8,
  className = '',
  contentClassName = '',
  triggerLabel,
}: FloatingPopoverProps): JSX.Element {
  const [isOpen, setIsOpen] = React.useState(false);
  const arrowRef = React.useRef<SVGSVGElement>(null);

  const { refs, floatingStyles, context } = useFloating({
    placement,
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(offsetValue),
      flip({
        fallbackPlacements: ['top', 'bottom', 'left', 'right'],
      }),
      shift({
        padding: 8,
      }),
      arrow({
        element: arrowRef,
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  return (
    <>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className={className}
        {...(triggerLabel && { role: 'button', tabIndex: 0, 'aria-label': triggerLabel })}
      >
        {children}
      </div>

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={classNames(styles.FloatingContent, contentClassName)}
          >
            {content}
            <FloatingArrow ref={arrowRef} context={context} className={styles.FloatingArrow} />
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
